// ---------------------------------------------------------------------------
// Partner reserve after a Flywire payment: hub POST /api/booking/reserve.
// Keep byte-identical in EXTRANET-VT and EXTRANET-SH.
//
// The hubs refuse partner sessions on /api/booking/create (2026-10-06). /reserve
// admits them, is payment-gated (a VERIFIED Flywire callback must match
// payment.ref) and books once per payment.ref, so no Idempotency-Key is needed.
// Hub contract: SHub api/controllers/guestBooking.js `reserve`.
//
// No imports on purpose: a plain node script can test it.
// ---------------------------------------------------------------------------

/** Backoff before each retry of a 402 (the verified webhook can lag the redirect). */
export const PAYMENT_RETRY_DELAYS_MS = [5000, 10000, 20000]

const addDays = (iso, n) => {
    const d = new Date(`${iso}T00:00:00Z`)
    if (Number.isNaN(d.getTime())) return undefined
    d.setUTCDate(d.getUTCDate() + n)
    return d.toISOString().slice(0, 10)
}

/** Checkout of a stored pending booking: check_out, else start_date + nights (older pendings). */
export const pendingCheckOut = (pending) => {
    if (!pending) return undefined
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(pending.check_out || ''))) return pending.check_out
    const nights = Math.floor(Number(pending.nights))
    if (!(nights >= 1) || !/^\d{4}-\d{2}-\d{2}$/.test(String(pending.start_date || ''))) return undefined
    return addDays(pending.start_date, nights)
}

/** The /api/booking/reserve body for a stored pending booking + the Flywire return params. */
export const buildReservePayload = (pending, { reference, confirmation } = {}) => {
    const p = pending || {}
    const rawId = String(p.listing_id || '')
    const g = p.guest || {}
    const guest = { name: String(g.name || '').trim(), email: String(g.email || '').trim() }
    const phone = String(g.phone || '').trim()
    const country = String(g.country || (p.vtbe && p.vtbe.guestCountry) || '').trim()
    if (phone) guest.phone = phone
    if (country) guest.country = country
    const body = {
        listingId: rawId.startsWith('BP-') ? rawId : `BP-${rawId}`,
        checkIn: p.start_date,
        checkOut: pendingCheckOut(p),
        guests: Number(p.number_of_guests),
        currency: p.currency,
        quoteTotal: Number(p.netTotal), // the NET the partner was quoted, not the marked-up charge
        guest,
        payment: { ref: String(reference || confirmation || ''), status: 'success' },
    }
    if (p.holdId) body.holdId = String(p.holdId)
    if (confirmation) body.referenceId = String(confirmation)
    if (p.comments) body.comments = String(p.comments)
    return body
}

/**
 * What one /reserve answer means → { outcome, code, message }:
 *   'confirmed'        201 ok, or 200 duplicate (replay of the same payment.ref);
 *   'payment_pending'  402 (payment not verified yet / payment_short / payment_unverifiable): retry with backoff;
 *   'rejected'         409 price_changed / quote_unavailable / just_booked / hold_expired, 502 channel
 *                      refusal, other 4xx: a definite no, nothing booked;
 *   'unknown'          504, other 5xx, no response, or a 409 replay of an attempt still pending/unknown:
 *                      the channel MAY hold the booking, so never rebook.
 */
export const classifyReserveResponse = ({ status, body } = {}) => {
    const b = body && typeof body === 'object' ? body : {}
    const code = String(b.code || (typeof b.error === 'string' ? b.error : '') || '')
    const message = String(b.message || b.detail || b.error || '')
    if ((status === 200 || status === 201) && b.ok === true) return { outcome: 'confirmed', code, message, reservationId: String(b.reservationId || '') }
    if (status === 402) return { outcome: 'payment_pending', code, message }
    if (status === 409 && /already exists/i.test(message) && !/failed_channel/i.test(message)) return { outcome: 'unknown', code, message }
    if (status === 502) return { outcome: 'rejected', code: code || 'channel_rejected', message }
    if (status >= 400 && status < 500) return { outcome: 'rejected', code, message }
    return { outcome: 'unknown', code: code || (status ? `http_${status}` : 'no_response'), message }
}
